import { useState, useEffect } from 'react';
import { ArrowLeft, Save } from 'lucide-react';
import { authAPI } from '../../services/api';

export default function NotificationSettings() {
  const [preferences, setPreferences] = useState({
    recieve_email_notification: true,
    recieve_sms_notification: true,
    recieve_in_app_notifications: true,
    recieve_promotional_emails: false,
    notify_on_new_message: true,
    notify_on_new_order: true,
    notify_on_scheduled_appointment: true
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    fetchNotificationPreferences();
  }, []);

  const fetchNotificationPreferences = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authAPI.getNotificationPreferences();
      if (response.success) {
        setPreferences(response.data);
      } else {
        setError(response.message || 'Failed to fetch notification preferences');
      }
    } catch (err) {
      console.error('Error fetching notification preferences:', err);
      setError('Failed to fetch notification preferences. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      const response = await authAPI.updateNotificationPreferences(preferences);
      
      if (response.success) {
        setSuccess('Notification preferences updated successfully!');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(response.message || 'Failed to update notification preferences');
      }
    } catch (err) {
      console.error('Error updating notification preferences:', err);
      setError('Failed to update notification preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const NotificationToggle = ({ label, description, preferenceKey }) => (
    <div className="notification-item-apns">
      <div className="notification-info-apns">
        <h4 className="notification-label-apns">{label}</h4>
        <p className="notification-description-apns">{description}</p>
      </div>
      <div className="notification-toggle-apns">
        <label className="toggle-switch-apns">
          <input
            type="checkbox"
            checked={preferences[preferenceKey]}
            onChange={() => handleToggle(preferenceKey)}
            disabled={loading || saving}
          />
          <span className="toggle-slider-apns"></span>
        </label>
      </div>
    </div>
  );

  return (
    <div className="notification-settings-container-apns">
      <div className="notification-settings-header-apns">
        <button className="back-button-apns" onClick={() => window.history.back()}>
          <ArrowLeft size={20} />
          <span>Back to Settings</span>
        </button>
        <h1 className="notification-settings-title-apns">Notification Settings</h1>
      </div>

      {loading ? (
        <div className="loading-notifications-apns">
          <p>Loading notification preferences...</p>
        </div>
      ) : error ? (
        <div className="error-notifications-apns">
          <p>{error}</p>
          <button onClick={fetchNotificationPreferences} className="retry-button-apns">Retry</button>
        </div>
      ) : (
        <>
          {success && (
            <div className="success-alert-apns">
              <div className="success-alert-content-apns">
                <div className="success-alert-icon-apns">✓</div>
                <div className="success-alert-message-apns">{success}</div>
                <button 
                  className="success-alert-close-apns"
                  onClick={() => setSuccess(null)}
                >
                  ×
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="error-alert-apns">
              <div className="error-alert-content-apns">
                <div className="error-alert-icon-apns">!</div>
                <div className="error-alert-message-apns">{error}</div>
                <button 
                  className="error-alert-close-apns"
                  onClick={() => setError(null)}
                >
                  ×
                </button>
              </div>
            </div>
          )}

          <div className="notification-sections-apns">
            <div className="notification-section-apns">
              <h3 className="section-title-apns">General Notifications</h3>
              <div className="notification-list-apns">
                <NotificationToggle
                  label="Email Notifications"
                  description="Receive notifications via email"
                  preferenceKey="recieve_email_notification"
                />
                <NotificationToggle
                  label="SMS Notifications"
                  description="Receive notifications via SMS"
                  preferenceKey="recieve_sms_notification"
                />
                <NotificationToggle
                  label="In-App Notifications"
                  description="Receive notifications within the app"
                  preferenceKey="recieve_in_app_notifications"
                />
                <NotificationToggle
                  label="Promotional Emails"
                  description="Receive promotional emails and offers"
                  preferenceKey="recieve_promotional_emails"
                />
              </div>
            </div>

            <div className="notification-section-apns">
              <h3 className="section-title-apns">Activity Notifications</h3>
              <div className="notification-list-apns">
                <NotificationToggle
                  label="New Messages"
                  description="Get notified when you receive new messages"
                  preferenceKey="notify_on_new_message"
                />
                <NotificationToggle
                  label="New Orders"
                  description="Get notified about new orders and updates"
                  preferenceKey="notify_on_new_order"
                />
                <NotificationToggle
                  label="Scheduled Appointments"
                  description="Get reminded about upcoming appointments"
                  preferenceKey="notify_on_scheduled_appointment"
                />
              </div>
            </div>
          </div>

          <div className="save-changes-container-apns">
            <button 
              className="save-changes-button-apns"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </>
      )}

      <style jsx>{`

        .notification-settings-header-apns {
          margin-bottom: 30px;
        }

        .back-button-apns {
          display: flex;
          align-items: center;
          gap: 8px;
          background: none;
          border: none;
          color: #6b7280;
          cursor: pointer;
          font-size: 14px;
          margin-bottom: 20px;
          padding: 8px 0;
        }

        .back-button-apns:hover {
          color: #0e4410;
        }

        .notification-settings-title-apns {
          font-size: 28px;
          font-weight: 600;
          color: #1f2937;
          margin: 0;
        }

        .loading-notifications-apns, .error-notifications-apns {
          text-align: center;
          padding: 40px 20px;
          color: #6b7280;
        }

        .error-notifications-apns {
          color: #ef4444;
        }

        .retry-button-apns {
          background: #0e4410;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 6px;
          cursor: pointer;
          margin-top: 10px;
        }

        .retry-button-apns:hover {
          background: #0a350c;
        }

        /* Alert Styles */
        .success-alert-apns, .error-alert-apns {
          position: fixed;
          top: 20px;
          right: 20px;
          z-index: 1001;
          animation: slideInRight 0.3s ease-out;
        }

        .success-alert-content-apns, .error-alert-content-apns {
          border-radius: 8px;
          padding: 16px 20px;
          display: flex;
          align-items: center;
          gap: 12px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          min-width: 300px;
        }

        .success-alert-content-apns {
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
        }

        .error-alert-content-apns {
          background: #fef2f2;
          border: 1px solid #fecaca;
        }

        .success-alert-icon-apns {
          background: #22c55e;
          color: white;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          font-size: 14px;
          flex-shrink: 0;
        }

        .error-alert-icon-apns {
          background: #ef4444;
          color: white;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          font-size: 14px;
          flex-shrink: 0;
        }

        .success-alert-message-apns {
          color: #166534;
          font-weight: 500;
          flex: 1;
        }

        .error-alert-message-apns {
          color: #dc2626;
          font-weight: 500;
          flex: 1;
        }

        .success-alert-close-apns, .error-alert-close-apns {
          background: none;
          border: none;
          font-size: 20px;
          cursor: pointer;
          padding: 0;
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .success-alert-close-apns {
          color: #166534;
        }

        .error-alert-close-apns {
          color: #dc2626;
        }

        .success-alert-close-apns:hover {
          color: #0f5132;
        }

        .error-alert-close-apns:hover {
          color: #b91c1c;
        }

        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        .notification-sections-apns {
          display: flex;
          flex-direction: column;
          gap: 30px;
        }

        .notification-section-apns {
          background: white;
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .section-title-apns {
          font-size: 18px;
          font-weight: 600;
          color: #1f2937;
          margin: 0 0 20px 0;
        }

        .notification-list-apns {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .notification-item-apns {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 0;
          border-bottom: 1px solid #f3f4f6;
        }

        .notification-item-apns:last-child {
          border-bottom: none;
        }

        .notification-info-apns {
          flex: 1;
        }

        .notification-label-apns {
          font-size: 16px;
          font-weight: 500;
          color: #1f2937;
          margin: 0 0 4px 0;
        }

        .notification-description-apns {
          font-size: 14px;
          color: #6b7280;
          margin: 0;
        }

        .notification-toggle-apns {
          margin-left: 20px;
        }

        .toggle-switch-apns {
          position: relative;
          display: inline-block;
          width: 50px;
          height: 24px;
        }

        .toggle-switch-apns input {
          opacity: 0;
          width: 0;
          height: 0;
        }

        .toggle-slider-apns {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #ccc;
          transition: 0.4s;
          border-radius: 24px;
        }

        .toggle-slider-apns:before {
          position: absolute;
          content: "";
          height: 18px;
          width: 18px;
          left: 3px;
          bottom: 3px;
          background-color: white;
          transition: 0.4s;
          border-radius: 50%;
        }

        .toggle-switch-apns input:checked + .toggle-slider-apns {
          background-color: #0e4410;
        }

        .toggle-switch-apns input:checked + .toggle-slider-apns:before {
          transform: translateX(26px);
        }

        .toggle-switch-apns input:disabled + .toggle-slider-apns {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .save-changes-container-apns {
          margin-top: 30px;
        }

        .save-changes-button-apns {
          background: #0e4410;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .save-changes-button-apns:hover:not(:disabled) {
          background: #0a350c;
        }

        .save-changes-button-apns:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
}